import { useState, forwardRef } from 'react';

import { Popover } from 'react-tiny-popover';

import CustomIcon from '@components/utils/CustomIcon';
import ErrorMessage from '@components/ErrorMessage';
import { useStableCallback } from '@hooks/useStableCallback';
import ErrorBoundary from '@components/utils/ErrorBoundary';
import useIsMounted from '@hooks/useIsMounted';

import {
  StyledSearchBarWrapper,
  SearchBarLabel,
  InputContentWrapper,
  IconRightSpace,
  SearchBarInputText,
  RenderOverlayContentWrapper,
  SearchBarInputWrapper,
  LocationAnimationErrorBox,
} from './SearchBarInputStyle';
import {
  SearchBarInputProps,
  SearchBarItemProps,
} from './SearchBarInputInterface';

export const SearchBarItem = ({
  leftIcon,
  isActive,
  label,
  InputComponent,
  isFilled,
  tour,
}: React.PropsWithChildren<SearchBarItemProps>) => (
  <>
    {leftIcon ? (
      <>
        <CustomIcon fontSize={20} icon={leftIcon} />
        <IconRightSpace />
      </>
    ) : null}

    <InputContentWrapper maxWidth={tour}>
      <SearchBarLabel isActive={isActive} isFilled={isFilled}>
        {label}
      </SearchBarLabel>
      {InputComponent ? (
        <SearchBarInputText isActive={isActive} isFilled={isFilled}>
          {InputComponent}
        </SearchBarInputText>
      ) : null}
    </InputContentWrapper>
  </>
);

export interface CustomPopoverChildComponentProps
  extends React.ComponentPropsWithoutRef<'div'> {
  onClick(): void;
  borderRadius?: string;
}

const CustomPopoverChildComponent = forwardRef<
  HTMLDivElement,
  CustomPopoverChildComponentProps
>((props, ref) => (
  <StyledSearchBarWrapper
    borderRadius={props.borderRadius}
    ref={ref}
    onClick={props.onClick}
  >
    {props.children}
  </StyledSearchBarWrapper>
));

const SearchBarInput = ({
  label,
  leftIcon,
  rightIcon,
  InputComponent,
  renderOverlayContent,
  error = false,
  errorMessage = '',
  hasAnimationOnError,
  isFilled,
  tourStyle,
  tourListing,
  isFlight,
  isBookerSearch = false,
  isFlightIconShow = false,
  disabled = false,
  tour = false,
  popoverProps: {
    //defaultIsOpen and isOpen should not be set together.
    isOpen, //controlled popover
    defaultIsOpen, //un-controlled popover
    padding = 10,
    onVisibleChange,
    onClickOutside,
    ...otherPopoverProps
  } = {},
  wrapperPadding = '17px',
  borderRadius,
  isHomePage,
}: React.PropsWithChildren<SearchBarInputProps>) => {
  const [isOverlayOpen, setIsOverlayOpen] = useState<boolean>(!!defaultIsOpen);

  const isControlledPopover = typeof isOpen === 'boolean';

  const closeOverlay = useStableCallback(() => {
    isControlledPopover ? onVisibleChange?.(false) : setIsOverlayOpen(false);
  });

  const toggleOverlay = useStableCallback(() => {
    isControlledPopover
      ? setTimeout(() => onVisibleChange?.(!isOpen))
      : setTimeout(() => setIsOverlayOpen(!isOverlayOpen));
  });

  const isOverlayVisible = disabled
    ? false
    : isControlledPopover
    ? !!isOpen
    : isOverlayOpen;

  const [parentRef, parentElement] = useIsMounted<HTMLDivElement>();

  return (
    <div ref={parentRef} style={{ width: '100%' }}>
      <ErrorBoundary>
        {parentElement ? (
          <Popover
            positions={['bottom', 'left']}
            align='start'
            padding={padding}
            containerStyle={{
              position: isBookerSearch ? 'relative' : 'fixed',
              zIndex: '101',
              backgroundColor: 'white',
              width: tourListing ? '818px' : tourStyle ? '1098px' : '570px',
              display: 'flex',
              justifyContent: 'center',
              margin: '0px',
              boxShadow: isBookerSearch
                ? ''
                : '0 20px 40px 0 rgba(0, 0, 0, 0.2)',
              borderRadius: '8px',
            }}
            reposition={false}
            parentElement={parentElement}
            content={() => {
              return renderOverlayContent ? (
                <RenderOverlayContentWrapper>
                  {renderOverlayContent(closeOverlay)}
                </RenderOverlayContentWrapper>
              ) : (
                <div />
              );
            }}
            isOpen={isOverlayVisible}
            onClickOutside={(e) => {
              onClickOutside?.(e);
              closeOverlay();
            }}
            {...otherPopoverProps}
          >
            <CustomPopoverChildComponent
              onClick={toggleOverlay}
              borderRadius={borderRadius}
            >
              <LocationAnimationErrorBox
                borderRadius={borderRadius}
                hasError={error}
                hasAnimationOnError={hasAnimationOnError}
              >
                <SearchBarInputWrapper
                  isHomePage={isHomePage}
                  wrapperPadding={wrapperPadding}
                >
                  <SearchBarItem
                    tour={tour}
                    isFlight={isFlight}
                    label={label}
                    leftIcon={leftIcon}
                    rightIcon={rightIcon}
                    InputComponent={InputComponent}
                    isActive={isOverlayVisible}
                    isFilled={isFilled}
                    isFlightIconShow={isFlightIconShow}
                  />
                </SearchBarInputWrapper>
              </LocationAnimationErrorBox>
              {error && errorMessage ? (
                <ErrorMessage message={errorMessage} showIcon={false} />
              ) : null}
            </CustomPopoverChildComponent>
          </Popover>
        ) : null}
      </ErrorBoundary>
    </div>
  );
};

export default SearchBarInput;
